import {
  PORTAL_HEADER_SECTIONS,
  PORTAL_LAST_TAB_MESSAGE,
  PORTAL_TAB_SECTIONS,
  isLastVisiblePortalTab,
  isPortalSectionHidden,
  type PortalHiddenSectionsLike,
  type PortalSection,
} from '@gc/shared';

import type { FieldError } from './useSettings';

/**
 * The Settings -> Portal "Portal sections" card, as data (owner, 2026-09-28:
 * "i want to be able to hide sections on the portal").
 *
 * PURE ON PURPOSE, like `settingsSearch.ts`: the rules that matter here — which
 * switch is ticked, which one may not be unticked, what the refusal says — are
 * all decided by the shared helpers in `@gc/shared/portal-sections`, and the
 * card only draws what this returns. Testing it needs no DOM, and the card
 * cannot grow a second opinion about the last-tab rule the API enforces.
 */

/** The two groups the card shows, in the order the PCs lay them out. */
export const PORTAL_SECTION_GROUPS = [
  // The nav's tabs. At least one must stay: the kiosk needs somewhere to land.
  { id: 'tabs', sections: PORTAL_TAB_SECTIONS },
  // The header's buttons. Not places, so both may go.
  { id: 'header', sections: PORTAL_HEADER_SECTIONS },
] as const;

export type PortalSectionGroupId = (typeof PORTAL_SECTION_GROUPS)[number]['id'];

export interface PortalSectionRow {
  section: PortalSection;
  /** Shown on the PCs, i.e. the box is ticked. The column stores the opposite. */
  shown: boolean;
  /**
   * The one tab still showing. Its switch is DISABLED rather than refused on
   * save: an owner who unticks it and presses Confirm should never have to
   * read "Keep at least one portal tab visible" to learn what the page knew.
   */
  locked: boolean;
}

/**
 * One row per section in `sections`, read off the stored hidden list.
 *
 * The list goes through the shared helpers, which parse it: an id a later
 * build added reads as nothing, and a hand-edited row that hides all six tabs
 * reads with Home shown — so the ticks here are what the PCs actually show.
 */
export function portalSectionRows(
  hidden: PortalHiddenSectionsLike,
  sections: readonly PortalSection[],
): PortalSectionRow[] {
  return sections.map((section) => ({
    section,
    shown: !isPortalSectionHidden(hidden, section),
    locked: isLastVisiblePortalTab(hidden, section),
  }));
}

/** The field the API's refusals name — see `updateGeneralSettingsSchema`. */
export const PORTAL_SECTIONS_FIELD = 'portalHiddenSections';

/**
 * The save refusal to show on the card, if any, in the reader's language.
 *
 * Two server shapes land here. The service and the route's ZodPipe both name
 * the field itself for the last-tab rule; a zod enum failure on one entry
 * names its INDEX (`portalHiddenSections.3`), and matching only the bare
 * field would drop that one on the floor — highlighted nowhere, said nowhere.
 *
 * The last-tab refusal is the API's fixed English sentence, shared as
 * `PORTAL_LAST_TAB_MESSAGE`; it is swapped for the translated one so an owner
 * reading Arabic or Kurdish is not answered in English. Anything else is
 * shown as sent.
 */
export function portalSectionsError(
  errors: readonly FieldError[],
  translate: (key: string) => string,
): string | undefined {
  const issue = errors.find(
    (error) =>
      error.field === PORTAL_SECTIONS_FIELD || error.field.startsWith(`${PORTAL_SECTIONS_FIELD}.`),
  );
  if (!issue) return undefined;
  return issue.message === PORTAL_LAST_TAB_MESSAGE
    ? translate('settings.general.portalSections.lastTabRefused')
    : issue.message;
}
